import { AppDispatch, RootState } from "../../../apis/store";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { toGetJobs } from "../../../apis/actions/job.actions";
import parse from "html-react-parser";
import { Modal } from "antd";
import { TbUsersGroup, TbBuilding, TbArrowUpRight } from "react-icons/tb";
import ApplyJob from "./ApplyJob";
import { useTranslation } from "react-i18next";

type Job = {
  id: number;
  name: string;
  description?: string;
  no_of_recruitment?: number;
  department_id?: string | number;
};

const descriptionPreview = (html: string) => {
  const element = document.createElement("div");
  element.innerHTML = html;
  return element.textContent?.replace(/\s+/g, " ").trim() ?? "";
};

const JobsCard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { list, status } = useSelector((state: RootState) => state.jobs.jobs);
  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState<Job | null>(null);
  const [detailsJob, setDetailsJob] = useState<Job | null>(null);

  useEffect(() => {
    dispatch(toGetJobs(i18n.language));
  }, [dispatch, i18n.language]);

  const jobs: Job[] = Array.isArray(list) ? list : [];

  return (
    <section className="ac-jobs" dir={i18n.dir()}>
      <div className="container">
        <header className="ac-jobs__intro">
          <span className="ac-jobs__eyebrow">{t("jobs.opportunities")}</span>
          <h1>{t("jobs.jobs")}</h1>
          <p>{t("jobs.intro")}</p>
        </header>

        <div className="ac-jobs__list" aria-live="polite">
          {status === "idle" && <p className="ac-jobs__state">{t("btn.loading")}</p>}
          {status === "rejected" && <p className="ac-jobs__state">{t("jobs.loadError")}</p>}
          {status === "succedded" && jobs.length === 0 && (
            <p className="ac-jobs__state">{t("jobs.empty")}</p>
          )}
          {jobs.map((job, index) => (
            <article
              className="ac-job"
              key={job.id ?? index}
              style={{ animationDelay: `${Math.min(index, 5) * 90}ms` }}
            >
              <div className="ac-job__content">
                <div className="ac-job__heading">
                  <span className="ac-job__marker" aria-hidden="true" />
                  <h2>{job.name}</h2>
                </div>
                {job.description && (
                  <>
                    <p className="ac-job__preview">{descriptionPreview(job.description)}</p>
                    <button
                      type="button"
                      className="ac-job__details"
                      onClick={() => setDetailsJob(job)}
                    >
                      {t("jobs.viewDetails")}
                    </button>
                  </>
                )}
              </div>
              <aside className="ac-job__aside">
                <div className="ac-job__facts">
                  {job.department_id != null && job.department_id !== "" && (
                    <div className="ac-job__fact">
                      <TbBuilding aria-hidden="true" />
                      <span><small>{t("jobs.department")}</small>{job.department_id}</span>
                    </div>
                  )}
                  {job.no_of_recruitment != null && (
                    <div className="ac-job__fact">
                      <TbUsersGroup aria-hidden="true" />
                      <span><small>{t("jobs.recruits")}</small>{job.no_of_recruitment}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="ac-job__apply"
                  onClick={() => {
                    setSelectedData(job);
                    setShowModal(true);
                  }}
                >
                  {t("btn.apply")}
                  <TbArrowUpRight aria-hidden="true" />
                </button>
              </aside>
            </article>
          ))}
        </div>
      </div>
      <ApplyJob
        showModal={showModal}
        setShowModal={setShowModal}
        selectedData={selectedData}
      />
      <Modal
        open={detailsJob !== null}
        onCancel={() => setDetailsJob(null)}
        footer={null}
        title={detailsJob?.name}
        width={720}
        className="ac-job-details-modal"
      >
        <div className="ac-job__description">
          {detailsJob?.description && parse(detailsJob.description)}
        </div>
        <button
          type="button"
          className="ac-job__apply ac-job-details-modal__apply"
          onClick={() => {
            setSelectedData(detailsJob);
            setDetailsJob(null);
            setShowModal(true);
          }}
        >
          {t("btn.apply")}
          <TbArrowUpRight aria-hidden="true" />
        </button>
      </Modal>
    </section>
  );
};

export default JobsCard;
